import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MdArrowBack, MdScale, MdCategory, MdCalendarToday, MdLocationOn } from "react-icons/md";
import axios from "axios";

export default function CalibratedDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await axios.get("https://usibras-web-backend.onrender.com/api/fetch-calibration-data");
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        const found = list.find((r) => String(r.id) === String(id));
        if (!mounted) return;
        if (found) setRecord(found);
        else setError("Record not found");
      } catch (err) {
        if (!mounted) return;
        setError("Failed to load record");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => (mounted = false);
  }, [id]);

  const fmtNumber = (v, opts = {}) => {
    const n = Number(v);
    if (Number.isNaN(n)) return "—";
    return new Intl.NumberFormat("en-US", opts).format(n);
  };

  if (loading) return (
    <div className="p-6">
      <p className="text-sm text-slate-500">Loading record…</p>
    </div>
  );

  if (error) return (
    <div className="p-6">
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg border">
        <MdArrowBack /> Back
      </button>
      <p className="text-sm text-red-600">{error}</p>
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border">
          <MdArrowBack /> Back
        </button>
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Calibration #{record.id}</h1>
          <p className="text-sm text-slate-500">{record.rcn_type || "RCN"} • {record.origin || "—"}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: big details */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-3">
              <MdCategory className="h-6 w-6 text-slate-700 dark:text-slate-200" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Sample details</h2>
              <p className="text-sm text-slate-500">Core attributes and measurement data</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <p className="text-xs uppercase text-slate-400">Type</p>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">{record.rcn_type || "—"}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-400">Origin</p>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">{record.origin || "—"}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-400">Size</p>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">{record.size || "—"}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-xs uppercase text-slate-400">Date</p>
                <p className="text-sm font-semibold text-slate-900 dark:text-white"><MdCalendarToday className="inline mr-1" />{record.created_at ? new Date(record.created_at).toLocaleString() : "—"}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-400">Zone</p>
                <p className="text-sm font-semibold text-slate-900 dark:text-white"><MdLocationOn className="inline mr-1" />{record.zone || "—"}</p>
              </div>
            </div>
          </div>

          {/* Raw JSON / notes */}
          {record.notes && (
            <div className="mt-6">
              <p className="text-xs uppercase text-slate-400">Notes</p>
              <p className="text-sm text-slate-800 dark:text-slate-200">{record.notes}</p>
            </div>
          )}
        </div>

        {/* Right: stats */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-3">
              <MdScale className="h-6 w-6 text-slate-700 dark:text-slate-200" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">Measurements</h3>
              <p className="text-xs text-slate-400">Key numeric values</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-400">Quantity</p>
              <p className="text-lg font-extrabold text-slate-900 dark:text-white">{record.real ? `${fmtNumber(record.real, {minimumFractionDigits:2, maximumFractionDigits:2})} kg` : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Bags</p>
              <p className="text-lg font-extrabold text-slate-900 dark:text-white">{record.number_of_bags ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Outturn</p>
              <p className="text-lg font-extrabold text-slate-900 dark:text-white">{record.outturn ? `${fmtNumber(record.outturn, {maximumFractionDigits:2})}%` : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Moisture</p>
              <p className="text-lg font-extrabold text-slate-900 dark:text-white">{record.moisture ? `${fmtNumber(record.moisture, {maximumFractionDigits:2})}%` : '—'}</p>
            </div>
          </div>

          <div className="mt-6">
            <p className="text-xs text-slate-400">Record ID</p>
            <p className="text-sm font-mono text-slate-700 dark:text-slate-300">{record.id}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
